import { useState } from "react";
import { Link, useParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Banknote, ExternalLink, MapPin, Phone, StickyNote } from "lucide-react";
import { formatMAD, formatMoroccanPhone } from "@tarawa/shared";
import { api } from "@/lib/api";
import { errorMessage, fmtTime, type OrderDetail } from "@/lib/driver";
import { useToast } from "@/lib/toast";
import { Button, Card, DeliveryBadge, ErrorBox, Loading, PaymentBadge, Sheet } from "@/components/ui";

const ACCEPTED = ["CONFIRMED", "PREPARING"];

export default function OrderDetailPage() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const [completeOpen, setCompleteOpen] = useState(false);
  const [failOpen, setFailOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [cashConfirmed, setCashConfirmed] = useState(false);
  const [reason, setReason] = useState("");

  const { data: order, isLoading, error, refetch } = useQuery({
    queryKey: ["order", id],
    queryFn: () => api<{ order: OrderDetail }>(`/orders/${id}`).then((r) => r.order),
    refetchInterval: 30_000,
  });

  const onDone = (o: { order: OrderDetail }, message: string) => {
    qc.setQueryData(["order", id], o.order);
    void qc.invalidateQueries({ queryKey: ["mine"] });
    void qc.invalidateQueries({ queryKey: ["summary"] });
    toast(message);
  };

  const start = useMutation({
    mutationFn: () => api<{ order: OrderDetail }>(`/orders/${id}/start`, { method: "POST" }),
    onSuccess: (o) => onDone(o, "Livraison commencée."),
    onError: (err) => toast(errorMessage(err), "error"),
  });

  const complete = useMutation({
    mutationFn: (amountCollectedCents: number) =>
      api<{ order: OrderDetail }>(`/orders/${id}/complete`, { method: "POST", body: { amountCollectedCents, cashCollected: true } }),
    onSuccess: (o) => {
      setCompleteOpen(false);
      onDone(o, "Livraison et paiement confirmés.");
    },
  });

  const fail = useMutation({
    mutationFn: () => api<{ order: OrderDetail }>(`/orders/${id}/fail`, { method: "POST", body: { reason: reason.trim() } }),
    onSuccess: (o) => {
      setFailOpen(false);
      onDone(o, "Échec de livraison enregistré.");
    },
  });

  if (isLoading) return <Loading />;
  if (error || !order) return <ErrorBox message={errorMessage(error)} onRetry={() => refetch()} />;

  const due = order.payment?.amountCents ?? order.totalCents;
  const d = order.delivery;
  const amountCents = Math.round(Number(amount.replace(",", ".")) * 100);
  const amountValid = amount.trim() !== "" && Number.isFinite(amountCents) && amountCents === due;

  return (
    <div className="space-y-4">
      <Link to="/mes-commandes" className="inline-flex items-center gap-1 text-sm font-semibold text-muted hover:text-ink">
        <ArrowLeft className="size-4" />
        Mes commandes
      </Link>

      <Card className="space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h1 className="text-xl font-extrabold">{order.orderNumber}</h1>
            <p className="text-xs text-muted">Acceptée {fmtTime(order.driverAssignedAt)}</p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <DeliveryBadge status={order.status} />
            {order.payment && <PaymentBadge status={order.payment.status} />}
          </div>
        </div>
        <div className="flex items-center justify-between rounded-2xl bg-saffron-50 px-4 py-3">
          <span className="flex items-center gap-2 text-sm font-semibold text-saffron-600">
            <Banknote className="size-5" />
            {order.payment?.status === "PAID" ? "Encaissé" : "Montant à encaisser (espèces)"}
          </span>
          <span className="text-xl font-extrabold text-ink">{formatMAD(order.payment?.collectedAmountCents ?? due)}</span>
        </div>
        {order.status === "DELIVERY_FAILED" && order.deliveryFailureReason && (
          <p role="status" className="rounded-2xl bg-terracotta-50 px-4 py-3 text-sm text-terracotta-600">
            Échec de livraison : « {order.deliveryFailureReason} ». L'administrateur décidera de la suite.
          </p>
        )}
      </Card>

      <Card className="space-y-3 text-sm">
        <h2 className="font-bold">Client et adresse</h2>
        <p className="font-semibold">{order.customer.name}</p>
        <a href={`tel:${order.customer.phone}`} className="inline-flex h-11 items-center gap-2 rounded-2xl bg-brand-50 px-4 font-semibold text-brand-800">
          <Phone className="size-4" />
          {formatMoroccanPhone(order.customer.phone)}
        </a>
        <p className="flex items-start gap-2">
          <MapPin className="mt-0.5 size-4 shrink-0 text-brand-600" />
          <span>
            {d.addressLine}, {d.city}
            {d.instructions && <span className="block text-muted">Indications : {d.instructions}</span>}
          </span>
        </p>
        {d.latitude != null && d.longitude != null && (
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${d.latitude},${d.longitude}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 font-semibold text-brand-700"
          >
            <ExternalLink className="size-4" />
            Position GPS ({d.latitude.toFixed(5)}, {d.longitude.toFixed(5)})
          </a>
        )}
        {order.notes && (
          <p className="flex gap-2 rounded-xl bg-canvas p-3">
            <StickyNote className="size-4 shrink-0 text-muted" />
            {order.notes}
          </p>
        )}
      </Card>

      <Card className="space-y-3">
        <h2 className="font-bold">Produits</h2>
        <ul className="divide-y divide-line text-sm">
          {order.items.map((i) => (
            <li key={i.id} className="flex justify-between gap-3 py-2">
              <span>
                <span className="me-2 inline-block min-w-7 rounded-lg bg-brand-50 px-1.5 py-0.5 text-center text-xs font-bold text-brand-800">{i.quantity}×</span>
                {i.productName}
                {i.packContents && (
                  <span className="mt-0.5 block text-xs text-muted">{i.packContents.map((c) => `${c.quantity}× ${c.name}`).join(" · ")}</span>
                )}
              </span>
              <span className="shrink-0 font-semibold">{formatMAD(i.lineTotalCents)}</span>
            </li>
          ))}
        </ul>
        <div className="flex justify-between border-t border-dashed border-line pt-3 font-extrabold">
          <span>Total</span>
          <span>{formatMAD(order.totalCents)}</span>
        </div>
      </Card>

      {/* Only the next permitted action is shown (the API enforces the same rules). */}
      <div className="sticky bottom-20 z-20 space-y-2 md:static">
        {ACCEPTED.includes(order.status) && (
          <Button size="lg" className="w-full shadow-[var(--shadow-float)]" onClick={() => start.mutate()} loading={start.isPending}>
            Commencer la livraison
          </Button>
        )}
        {order.status === "OUT_FOR_DELIVERY" && (
          <>
            <Button
              size="lg"
              className="w-full shadow-[var(--shadow-float)]"
              onClick={() => {
                setAmount("");
                setCashConfirmed(false);
                complete.reset();
                setCompleteOpen(true);
              }}
            >
              Confirmer la livraison et le paiement
            </Button>
            <Button
              variant="danger"
              size="lg"
              className="w-full"
              onClick={() => {
                setReason("");
                fail.reset();
                setFailOpen(true);
              }}
            >
              Échec de livraison
            </Button>
          </>
        )}
      </div>

      <Sheet open={completeOpen} onClose={() => setCompleteOpen(false)} title="Confirmer la livraison et le paiement">
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (amountValid && cashConfirmed) complete.mutate(amountCents);
          }}
        >
          <p className="text-sm text-muted">
            Montant dû : <span className="font-extrabold text-ink">{formatMAD(due)}</span>. Saisissez le montant réellement encaissé.
          </p>
          <label className="block space-y-1.5 text-sm font-semibold">
            Montant encaissé (DH)
            <input
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={String(due / 100)}
              className="block h-12 w-full rounded-2xl border border-line px-4 text-[16px] focus:border-brand-500 focus:ring-4 focus:ring-brand-500/15 focus:outline-none"
            />
          </label>
          {amount.trim() !== "" && !amountValid && <p className="text-sm font-medium text-terracotta-600">Le montant doit être exactement {formatMAD(due)}.</p>}
          <label className="flex items-start gap-3 text-sm font-medium">
            <input type="checkbox" checked={cashConfirmed} onChange={(e) => setCashConfirmed(e.target.checked)} className="mt-0.5 size-5 accent-brand-600" />
            Je confirme avoir livré la commande et reçu {formatMAD(due)} en espèces.
          </label>
          {complete.error && <ErrorBox message={errorMessage(complete.error)} />}
          <Button type="submit" size="lg" className="w-full" disabled={!amountValid || !cashConfirmed} loading={complete.isPending}>
            Confirmer
          </Button>
        </form>
      </Sheet>

      <Sheet open={failOpen} onClose={() => setFailOpen(false)} title="Échec de livraison">
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (reason.trim().length >= 3) fail.mutate();
          }}
        >
          <label className="block space-y-1.5 text-sm font-semibold">
            Motif (obligatoire)
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={300}
              rows={3}
              placeholder="Ex. : client injoignable, adresse introuvable…"
              className="block w-full rounded-2xl border border-line px-4 py-3 text-[16px] focus:border-brand-500 focus:ring-4 focus:ring-brand-500/15 focus:outline-none"
            />
          </label>
          <p className="text-xs text-muted">La commande ne sera ni livrée ni payée. L'administrateur décidera de la suite.</p>
          {fail.error && <ErrorBox message={errorMessage(fail.error)} />}
          <Button type="submit" variant="danger" size="lg" className="w-full" disabled={reason.trim().length < 3} loading={fail.isPending}>
            Enregistrer l'échec
          </Button>
        </form>
      </Sheet>
    </div>
  );
}
